import React from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import './InitiativeInterceptionButton.css';
import {showToast, withToastIcon, showToastWaitOtherPlayerTurn, toggleInitiativeInterception} from "../../../redux/actions";
import {checkTutorialActionWouldApply} from "../../../utils/tutorial/tutorialActionGate";
import {
  selectCurrentPlayer,
  selectGameType,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectPlayers,
  selectTurnOrder
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../../constants/phases";
import {
  checkIfHasRemainingConstructionActions
} from "../../../utils/gameState/construction/checkIfHasRemainingConstructionActions";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {useTutorialUiFlags} from "../../../utils/tutorial/useTutorialUiFlags";
import InitiativeInterceptionButtonCompact
  from "./InitiativeInterceptionButtonCompact/InitiativeInterceptionButtonCompact";
import {getActingPlayer} from "../../../utils/players/getActingPlayer";
import {selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import {isPlayerPolicy} from "../../../utils/factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {isPlayerNextPolicy} from "../../../utils/factions/isPlayerNextPolicy";
import {TOAST_ICONS} from "../../../constants/toast";

const InitiativeInterceptionButton = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const gameType = useSelector(selectGameType);
  const userId = useSelector(selectUserId);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const turnOrder = useSelector(selectTurnOrder);
  const phase = useSelector(selectPhase);
  const players = useSelector(selectPlayers);
  const pendingBuildings = useSelector(selectPendingBuildings);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const tutorialUiFlags = useTutorialUiFlags();
  const isInitiativeInterceptedWithIsolationism = useSelector(selectIsInitiativeInterceptedWithIsolationism);

  const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);

  // Показываем кнопку только в фазе строительства
  if (phase !== PHASES.CONSTRUCTION) {
    return null;
  }

  // В главах обучения без темы перехвата инициативы кнопка скрыта, чтобы не
  // отвлекать от сценария (в главе 2 перехват — часть программы)
  if (tutorialUiFlags.hideInitiativeInterception) {
    return null;
  }

  // Проверяем является ли текущий игрок первым в порядке хода (владеет инициативой)
  const isCurrentPlayerInitiativeOwner = turnOrder[0] === currentPlayer.id;

  const isActive = nextInitiativePlayerId === currentPlayer.id;

  const isOtherPlayerIntercepted = nextInitiativePlayerId && actingPlayer.id !== nextInitiativePlayerId;

  const currentInitiativeOwner = getPlayerById(players, turnOrder[0]);
  const isOwnerHasIsolationism = isPlayerPolicy(currentInitiativeOwner, POLICIES.ISOLATIONISM)
    || isPlayerNextPolicy(currentInitiativeOwner, POLICIES.ISOLATIONISM);

  const handleClick = () => {
    if (!isCurrentUserTurn) {
      dispatch(showToastWaitOtherPlayerTurn(currentPlayer));
      return;
    }

    // Гейт обучения: заблокированный перехват не должен звучать и показывать
    // тосты успеха/отмены — диспатчим (гейт покажет подсказку) и выходим
    const interceptionAction = toggleInitiativeInterception();
    if (!checkTutorialActionWouldApply(store.getState(), interceptionAction)) {
      dispatch(interceptionAction);
      return;
    }

    if (isCurrentPlayerInitiativeOwner) {
      dispatch(withToastIcon(showToast('Вы уже владеете инициативой!', 'info'), TOAST_ICONS.INITIATIVE));
      return;
    }

    if (isOtherPlayerIntercepted) {
      // Инициативу уже перехватил другой игрок
      const nextInitiativeOwner = getPlayerById(players, nextInitiativePlayerId);
      // Игрока называем по имени, а не по цвету: цвет — служебное поле, и в
      // тексте («orange уже перехватил») он читался как чужое прозвище
      if (nextInitiativeOwner) {
        dispatch(showToast(`Нельзя перехватить инициативу,\nтак как ${nextInitiativeOwner.name} уже перехватил инициативу в этом раунде!\n\nОн будет ходить первым в фазе Войны.`, 'warning', 5000));
      }
      return;
    }

    if (!isActive) {
      const hasActionsRemaining = checkIfHasRemainingConstructionActions(currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism);
      if (!hasActionsRemaining) {
        dispatch(showToast('У вас не осталось доступных действий для перехвата инициативы!', 'warning', 5000));
        return;
      }

      if (isOwnerHasIsolationism) {
        dispatch(showToast('Нельзя перехватить инициативу у игрока,\nкоторый владеет политикой Изоляционизм!', 'warning', 5000));
        return;
      }

      dispatch(withToastIcon(showToast('Вы перехватили инициативу!\n\nВы будете ходить первым в фазе Войны.', 'success', 5000, true), TOAST_ICONS.INITIATIVE));
      REGISTRY_SINGLETON.getSoundManager().playInitiativeInterception();
    } else {
      dispatch(withToastIcon(showToast('Вы отменили перехват инициативы.', 'info'), TOAST_ICONS.INITIATIVE));
    }

    dispatch(toggleInitiativeInterception());
  };

  // Только компактная кнопка в футере (свой ход строительства): кто
  // перехватил инициативу, видно по значку в строке игроков и в списке
  return (
    <InitiativeInterceptionButtonCompact
      isActive={isActive}
      isInitiativeOwner={isCurrentPlayerInitiativeOwner}
      isDisabled={!isActive && (isOtherPlayerIntercepted || !isCurrentUserTurn || isOwnerHasIsolationism)}
      handleClick={handleClick}
    />
  );
};

export default InitiativeInterceptionButton;
